import { useState } from "react";
import type { EnrichedLead } from "@/lib/data";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ScoreGauge, ScoreBreakdown, ScoreBadge } from "@/components/ScoreGauge";
import {
	ArrowLeft,
	Building2,
	MapPin,
	Users,
	DollarSign,
	Globe,
	Calendar,
	Layers,
	Mail,
	Phone,
	ExternalLink,
	Zap,
	TrendingUp,
	Sparkles,
	Lightbulb,
	ChevronRight,
} from "lucide-react";

function getSignalColor(strength: string) {
	switch (strength) {
		case "high": return "bg-red-50 text-red-700 border-red-200";
		case "medium": return "bg-amber-50 text-amber-700 border-amber-200";
		case "low": return "bg-slate-50 text-slate-600 border-slate-200";
		default: return "bg-slate-50 text-slate-600 border-slate-200";
	}
}

function getSignalStrengthDot(strength: string) {
	switch (strength) {
		case "high": return "bg-red-500";
		case "medium": return "bg-amber-500";
		case "low": return "bg-slate-400";
		default: return "bg-slate-400";
	}
}

function getSignalTypeLabel(type: string) {
	return type.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function timeAgo(timestamp: string): string {
	const diff = Date.now() - new Date(timestamp).getTime();
	const days = Math.floor(diff / (1000 * 60 * 60 * 24));
	if (days === 0) return "Today";
	if (days === 1) return "Yesterday";
	if (days < 7) return `${days}d ago`;
	return `${Math.floor(days / 7)}w ago`;
}

export function LeadDetail({
	lead,
	onBack,
}: {
	lead: EnrichedLead;
	onBack: () => void;
}) {
	const { company, contacts, signals, score } = lead;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground hover:text-foreground">
					<ArrowLeft className="w-4 h-4 mr-1" />
					Back
				</Button>
			</div>

			{/* Company Header Card */}
			<Card className="bg-white border-slate-200">
				<CardContent className="p-6">
					<div className="flex items-start gap-5">
						<div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-xl shadow-md">
							{company.logo}
						</div>
						<div className="flex-1 min-w-0">
							<div className="flex items-start justify-between gap-4">
								<div>
									<div className="flex items-center gap-3">
										<h1 className="text-2xl font-bold text-foreground">{company.name}</h1>
										<ScoreBadge score={score.overall} />
									</div>
									<p className="text-sm text-muted-foreground mt-1">{company.description}</p>
								</div>
								<ScoreGauge score={score} size="lg" />
							</div>

							<div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-muted-foreground">
								<span className="flex items-center gap-1.5">
									<Building2 className="w-4 h-4" />
									{company.industry}
								</span>
								<span className="flex items-center gap-1.5">
									<MapPin className="w-4 h-4" />
									{company.location}
								</span>
								<span className="flex items-center gap-1.5">
									<Users className="w-4 h-4" />
									{company.size} employees
								</span>
								<span className="flex items-center gap-1.5">
									<DollarSign className="w-4 h-4" />
									{company.revenue}
								</span>
								<span className="flex items-center gap-1.5">
									<Calendar className="w-4 h-4" />
									Founded {company.founded}
								</span>
								<span className="flex items-center gap-1.5">
									<Layers className="w-4 h-4" />
									{company.funding}
								</span>
							</div>

							<div className="flex flex-wrap gap-1.5 mt-4">
								{company.techStack.map((tech) => (
									<Badge key={tech} variant="secondary" className="text-xs bg-slate-100 text-slate-700 hover:bg-slate-100">
										{tech}
									</Badge>
								))}
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Tabs */}
			<Tabs defaultValue="overview" className="w-full">
				<TabsList className="bg-white border border-slate-200 p-1">
					<TabsTrigger value="overview" className="text-sm data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
						Overview
					</TabsTrigger>
					<TabsTrigger value="contacts" className="text-sm data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
						Contacts ({contacts.length})
					</TabsTrigger>
					<TabsTrigger value="signals" className="text-sm data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
						Signals ({signals.length})
					</TabsTrigger>
					<TabsTrigger value="scoring" className="text-sm data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
						AI Scoring
					</TabsTrigger>
				</TabsList>

				{/* Overview Tab */}
				<TabsContent value="overview" className="mt-4">
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
						{/* AI Recommendation */}
						<Card className="lg:col-span-2 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
							<CardContent className="p-5">
								<div className="flex items-start gap-3">
									<div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
										<Sparkles className="w-5 h-5 text-blue-600" />
									</div>
									<div>
										<h3 className="font-semibold text-blue-900">AI Recommendation</h3>
										<p className="text-sm text-blue-800 mt-1">{score.recommendation}</p>
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Quick Score */}
						<Card className="bg-white border-slate-200">
							<CardContent className="p-5">
								<h3 className="text-sm font-semibold text-muted-foreground mb-3">Lead Score</h3>
								<div className="flex items-center justify-center">
									<ScoreGauge score={score} size="lg" />
								</div>
							</CardContent>
						</Card>

						{/* Key Signals */}
						<Card className="lg:col-span-2 bg-white border-slate-200">
							<CardContent className="p-5">
								<h3 className="text-sm font-semibold text-muted-foreground mb-3">Recent Intent Signals</h3>
								<div className="space-y-3">
									{signals.slice(0, 3).map((signal) => (
										<div key={signal.id} className="flex items-start gap-3">
											<div className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${getSignalStrengthDot(signal.strength)}`} />
											<div className="flex-1 min-w-0">
												<div className="flex items-center justify-between">
													<span className="text-sm font-medium">{signal.title}</span>
													<span className="text-xs text-muted-foreground">{timeAgo(signal.timestamp)}</span>
												</div>
												<p className="text-xs text-muted-foreground mt-0.5">{signal.description}</p>
											</div>
										</div>
									))}
								</div>
							</CardContent>
						</Card>

						{/* Key Contacts */}
						<Card className="bg-white border-slate-200">
							<CardContent className="p-5">
								<h3 className="text-sm font-semibold text-muted-foreground mb-3">Key Contacts</h3>
								<div className="space-y-3">
									{contacts.slice(0, 3).map((contact) => (
										<div key={contact.id} className="flex items-center gap-3">
											<div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-xs font-medium text-slate-600">
												{contact.avatar}
											</div>
											<div className="min-w-0">
												<div className="text-sm font-medium truncate">{contact.name}</div>
												<div className="text-xs text-muted-foreground truncate">{contact.title}</div>
											</div>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					</div>
				</TabsContent>

				{/* Contacts Tab */}
				<TabsContent value="contacts" className="mt-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{contacts.map((contact) => (
							<Card key={contact.id} className="bg-white border-slate-200">
								<CardContent className="p-5">
									<div className="flex items-start gap-4">
										<div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-sm font-bold text-slate-600">
											{contact.avatar}
										</div>
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2">
												<h4 className="font-semibold text-foreground">{contact.name}</h4>
												<Badge variant="outline" className="text-[10px] py-0">{contact.seniority}</Badge>
											</div>
											<p className="text-sm text-muted-foreground">{contact.title}</p>
											<p className="text-xs text-muted-foreground mt-0.5">{contact.department}</p>

											<Separator className="my-3" />

											<div className="space-y-1.5">
												<a className="flex items-center gap-2 text-xs text-muted-foreground hover:text-blue-600 transition-colors">
													<Mail className="w-3 h-3" />
													{contact.email}
												</a>
												<a className="flex items-center gap-2 text-xs text-muted-foreground hover:text-blue-600 transition-colors">
													<Phone className="w-3 h-3" />
													{contact.phone}
												</a>
												<a className="flex items-center gap-2 text-xs text-muted-foreground hover:text-blue-600 transition-colors">
													<ExternalLink className="w-3 h-3" />
													linkedin.com/in/{contact.linkedin}
												</a>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				</TabsContent>

				{/* Signals Tab */}
				<TabsContent value="signals" className="mt-4">
					<Card className="bg-white border-slate-200">
						<CardContent className="p-5">
							<div className="space-y-4">
								{signals
									.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
									.map((signal) => (
										<div key={signal.id} className="flex items-start gap-4 p-4 rounded-lg border border-slate-100 hover:bg-slate-50/50 transition-colors">
											<div className={`flex-shrink-0 w-2.5 h-2.5 rounded-full mt-1 ${getSignalStrengthDot(signal.strength)}`} />
											<div className="flex-1 min-w-0">
												<div className="flex items-center justify-between gap-3">
													<div className="flex items-center gap-2">
														<h4 className="text-sm font-semibold">{signal.title}</h4>
														<span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full border ${getSignalColor(signal.strength)}`}>
															{signal.strength}
														</span>
													</div>
													<span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(signal.timestamp)}</span>
												</div>
												<p className="text-sm text-muted-foreground mt-1">{signal.description}</p>
												<Badge variant="secondary" className="mt-2 text-[10px] bg-slate-100 text-slate-600">
													{getSignalTypeLabel(signal.type)}
												</Badge>
											</div>
										</div>
									))}
							</div>
						</CardContent>
					</Card>
				</TabsContent>

				{/* AI Scoring Tab */}
				<TabsContent value="scoring" className="mt-4">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
						<Card className="bg-white border-slate-200">
							<CardContent className="p-5">
								<h3 className="text-sm font-semibold text-muted-foreground mb-4">Score Breakdown</h3>
								<div className="flex items-center justify-center mb-6">
									<ScoreGauge score={score} size="lg" />
								</div>
								<ScoreBreakdown score={score} />
							</CardContent>
						</Card>

						<Card className="bg-white border-slate-200">
							<CardContent className="p-5">
								<h3 className="text-sm font-semibold text-muted-foreground mb-4">
									<span className="flex items-center gap-1.5">
										<Lightbulb className="w-4 h-4" />
										Scoring Factors
									</span>
								</h3>
								<div className="space-y-3">
									{score.factors.map((factor, i) => (
										<div key={i} className="flex items-start gap-2.5">
											<ChevronRight className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
											<span className="text-sm text-foreground">{factor}</span>
										</div>
									))}
								</div>

								<Separator className="my-5" />

								<div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
									<div className="flex items-start gap-2.5">
										<Sparkles className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
										<div>
											<div className="text-xs font-semibold text-blue-900 mb-1">AI Recommendation</div>
											<p className="text-sm text-blue-800">{score.recommendation}</p>
										</div>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</TabsContent>
			</Tabs>
		</div>
	);
}
